/**
 * AIReviewComponent
 *
 * Component for reviewing AI-extracted diagnoses and measurements.
 * Users can view source text evidence and approve/reject suggestions
 * before data is applied to the patient record.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Checkbox,
  Collapse,
  IconButton,
  Divider,
  Paper,
  Menu,
  MenuItem,
  LinearProgress,
  TextField,
  Dialog,
  DialogContent,
} from '@mui/material'
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Refresh as RefreshIcon,
  PlayArrow as ExtractIcon,
  Description as DocumentIcon,
  Science as MeasurementIcon,
  LocalHospital as DiagnosisIcon,
  KeyboardArrowDown as ArrowDownIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  Undo as UndoIcon,
} from '@mui/icons-material'
import { toast } from 'react-toastify'
import { ClassNameMap, withStyles } from '@mui/styles'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import stylesheet from '../detail-container.stylesheet'

import { aiExtractionService } from '../../../../services/ai-extraction.service'
import {
  AIExtractedSuggestion,
  AIExtractionLog,
  ExtractionType,
  RejectionCategory,
  MEAS_REJECTION_CATEGORIES,
  DX_REJECTION_CATEGORIES,
} from '../../../../model/ai-extraction.types'
import { formatDisplayName } from '../../../../utils/display-name-formatter'

interface AIReviewProps {
  patientId: string
  patientRefId: string
  classes: ClassNameMap
  onDataChanged?: () => void
}

type TabValue = 'all' | 'diagnosis' | 'measurement'

const AIReviewComponent: React.FC<AIReviewProps> = ({ patientId: _patientId, patientRefId, classes, onDataChanged }) => {
  // State
  const [loading, setLoading] = useState(false)
  const [extracting, setExtracting] = useState(false)
  const [suggestions, setSuggestions] = useState<AIExtractedSuggestion[]>([])
  const [extractionLog, setExtractionLog] = useState<AIExtractionLog | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Set when the extractor read only PART of a source file. Separate from `error` on purpose:
  // the extraction did not fail, so it is not an error — it is a statement about how much of
  // the record these suggestions represent, and the reviewer needs it in front of them for the
  // whole approval pass, not for the life of a toast. See CAPA F272.
  const [incompleteRead, setIncompleteRead] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  const [approving, setApproving] = useState(false)

  // Local review state — save-as-you-go without refetching the whole list.
  // Reviewed items stay rendered in place (marked + optionally undoable) so the
  // reviewer never loses their scroll/expansion/place. See REVIEWED_STATE below.
  const [reviewState, setReviewState] = useState<Record<number, { status: 'approved' | 'rejected'; categoryLabel?: string; canUndo: boolean }>>({})

  // Reviewer-supplied collection dates, keyed by suggestion id ('YYYY-MM-DD').
  // The extractor refuses to guess a date it could not read off the report — a value filed
  // under the wrong date is a wrong result, not a partial one. This is the recourse: the
  // person reviewing has the source PDF open and can read the real "Date Collected".
  const [dateEdits, setDateEdits] = useState<Record<number, string>>({})

  // Reject picker: array of suggestion ids being rejected (1 = per-item, >1 = bulk)
  const [rejectTarget, setRejectTarget] = useState<number[] | null>(null)
  const [rejectCategory, setRejectCategory] = useState<string>('')
  const [rejectCorrection, setRejectCorrection] = useState<string>('')

  // Zoomed source-region crop — click a crop to enlarge it (read the marker name at full res)
  const [zoomImage, setZoomImage] = useState<string | null>(null)

  // Deferred patient-data refresh: fire onDataChanged once when the panel closes
  // (unmounts) instead of after every action, so approving an item never reloads
  // the surrounding patient view mid-review.
  const onDataChangedRef = useRef(onDataChanged)
  const pendingRefreshRef = useRef(false)
  useEffect(() => {
    onDataChangedRef.current = onDataChanged
  }, [onDataChanged])
  useEffect(() => {
    return () => {
      if (pendingRefreshRef.current) {
        onDataChangedRef.current?.()
      }
    }
  }, [])

  // Extract menu state
  const [extractMenuAnchor, setExtractMenuAnchor] = useState<null | HTMLElement>(null)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Drag and drop state
  const [isDragOver, setIsDragOver] = useState(false)
  const dragCounter = useRef(0)

  // Staged files for batch processing
  const [stagedFiles, setStagedFiles] = useState<File[]>([])

  // Load suggestions on mount
  useEffect(() => {
    loadSuggestions()
  }, [patientRefId])

  const loadSuggestions = useCallback(async () => {
    if (!patientRefId) return

    setLoading(true)
    setError(null)

    try {
      const response = await aiExtractionService.getSuggestions(patientRefId)
      setSuggestions(response.data.suggestions || [])
      setExtractionLog(response.data.extraction_log)
      // A fresh pull replaces the working set — drop stale local review marks.
      setReviewState({})
      setRejectTarget(null)
      setSelectedIds(new Set())
    } catch (err: any) {
      console.error('Failed to load AI suggestions:', err)
      setError(err.response?.data?.error?.message || 'Failed to load suggestions')
    } finally {
      setLoading(false)
    }
  }, [patientRefId])

  const handleExtract = async (focus: ExtractionType) => {
    setExtractMenuAnchor(null)
    setExtracting(true)
    setError(null)

    try {
      const response = await aiExtractionService.extractDiagnoses(patientRefId, focus)
      toast.success(response.data.message || `Extracted ${response.data.total_suggestions} suggestions`)

      // Show skipped allergy details split by reason category
      const skipped = response.data.skipped_allergies
      if (skipped && skipped.length > 0) {
        const duplicates = skipped.filter((s) => s.reason.includes('already exists'))
        const noMatch = skipped.filter((s) => !s.reason.includes('already exists'))
        if (duplicates.length > 0) {
          toast.info(`${duplicates.length} already on file: ${duplicates.map((s) => s.name).join(', ')}`, { autoClose: 8000 })
        }
        if (noMatch.length > 0) {
          toast.warn(`${noMatch.length} non-medication allergens skipped: ${noMatch.map((s) => s.name).join(', ')}`, { autoClose: 8000 })
        }
      }

      await loadSuggestions()
    } catch (err: any) {
      console.error('Extraction failed:', err)
      const errMsg = err.response?.data?.error?.message || 'Extraction failed'
      setError(errMsg)
      toast.error(errMsg)
    } finally {
      setExtracting(false)
    }
  }

  // Approve one or more suggestions. Save-as-you-go: persist immediately, then mark
  // the items in place (NO full-list refetch, NO patient reload mid-review). Both
  // measurements and diagnoses can be undone in-panel (the /revert endpoint reverses
  // the created record and, for diagnoses, restores the prior answer).
  const handleApprove = async (suggestionIds: number[]) => {
    if (suggestionIds.length === 0) return

    // A measurement with no collection date is REFUSED at the write — the extractor will not
    // guess a date, because a wrong date is a wrong result (a stale value dated today becomes
    // the patient's current value). The reviewer has the source PDF, so they supply it. Block
    // here rather than let the server refuse, so the fix is one field away instead of a
    // toast about something that already failed.
    const undated = suggestions.filter(
      (s) => suggestionIds.includes(s.id) &&
             s.suggestion_type === 'measurement' &&
             !s.measurement_date && !dateEdits[s.id]?.trim()
    )
    if (undated.length > 0) {
      const names = undated.map((s) => s.measurement_name).filter(Boolean).join(', ')
      toast.error(
        `Enter the collection date from the report for: ${names}. ` +
        `An undated result cannot be charted — it would be filed as today's value.`
      )
      return
    }

    const date_overrides: Record<number, string> = {}
    suggestionIds.forEach((id) => {
      const d = dateEdits[id]?.trim()
      if (d) date_overrides[id] = d
    })

    setApproving(true)
    try {
      const response = await aiExtractionService.approveSuggestions(patientRefId, {
        approved_ids: suggestionIds,
        rejected_ids: [],
        date_overrides,
      })

      const { approved_count, created_measurements, created_questionnaire_responses, errors } = response.data
      toast.success(`Approved ${approved_count}. Created ${created_questionnaire_responses?.length || 0} diagnoses, ${created_measurements?.length || 0} measurements.`)
      // A refused write leaves the suggestion PENDING and reports why — never silently
      // "approved" with no record. Surface it so the reviewer can correct and retry.
      if (errors && errors.length) {
        errors.forEach((e: string) => toast.error(e))
      }

      const refused = new Set(
        (errors || []).map((e: string) => Number((e.match(/Suggestion (\d+)/) || [])[1]))
                      .filter((n: number) => !Number.isNaN(n))
      )
      setReviewState((prev) => {
        const next = { ...prev }
        suggestionIds.forEach((id) => {
          if (!refused.has(id)) next[id] = { status: 'approved', canUndo: true }
        })
        return next
      })
      setSelectedIds(new Set())
      pendingRefreshRef.current = true // a record was created — refresh patient view on close
    } catch (err: any) {
      console.error('Approval failed:', err)
      toast.error(err.response?.data?.error?.message || 'Approval failed')
    } finally {
      setApproving(false)
    }
  }

  // Open the structured reject picker for one item (per-item) or many (bulk).
  const openRejectPicker = (suggestionIds: number[]) => {
    if (suggestionIds.length === 0) return
    setRejectTarget(suggestionIds)
    setRejectCategory('')
    setRejectCorrection('')
  }

  const cancelReject = () => {
    setRejectTarget(null)
    setRejectCategory('')
    setRejectCorrection('')
  }

  // Categories valid for the current reject target. Mixed-type bulk selections fall
  // back to 'Other' only (the sole code shared across measurement + diagnosis).
  const getRejectCategories = (targetIds: number[]): RejectionCategory[] => {
    const types = new Set(suggestions.filter((s) => targetIds.includes(s.id)).map((s) => s.suggestion_type))
    if (types.size === 1) {
      return types.has('measurement') ? MEAS_REJECTION_CATEGORIES : DX_REJECTION_CATEGORIES
    }
    return [{ code: 'other', label: 'Other', allowsCorrection: false }]
  }

  // Confirm the reject: send the structured category (+ optional correction/note) and
  // mark items rejected in place. Rejects are always undoable (nothing is created).
  const confirmReject = async () => {
    if (!rejectTarget || !rejectCategory) return
    const ids = rejectTarget
    const isSingle = ids.length === 1
    const cats = getRejectCategories(ids)
    const cat = cats.find((c) => c.code === rejectCategory)
    const label = cat?.label || rejectCategory

    setApproving(true)
    try {
      const rejection_categories: Record<number, string> = {}
      const rejection_reasons: Record<number, string> = {}
      const corrections: Record<number, { corrected_answer?: string }> = {}
      const text = rejectCorrection.trim()
      ids.forEach((id) => {
        rejection_categories[id] = rejectCategory
        if (text) {
          // 'other' has no structured correction — store the text as the free-text note.
          // Otherwise the text is the value/date it should have been (retraining signal).
          if (rejectCategory === 'other') rejection_reasons[id] = text
          else if (cat?.allowsCorrection && isSingle) corrections[id] = { corrected_answer: text }
        }
      })

      const response = await aiExtractionService.approveSuggestions(patientRefId, {
        approved_ids: [],
        rejected_ids: ids,
        rejection_categories,
        rejection_reasons,
        corrections,
      })

      toast.info(`Rejected ${response.data.rejected_count} suggestion(s)`)
      setReviewState((prev) => {
        const next = { ...prev }
        ids.forEach((id) => {
          next[id] = { status: 'rejected', categoryLabel: label, canUndo: true }
        })
        return next
      })
      setSelectedIds(new Set())
      cancelReject()
    } catch (err: any) {
      console.error('Rejection failed:', err)
      toast.error(err.response?.data?.error?.message || 'Rejection failed')
    } finally {
      setApproving(false)
    }
  }

  // Undo a single reviewed item back to actionable. Backend reverses the created record
  // (deletes the measurement, or deletes the response + restores the prior answer for a
  // diagnosis) for approvals, or clears the reject metadata for rejects.
  const handleUndo = async (suggestionId: number) => {
    setApproving(true)
    try {
      await aiExtractionService.revertSuggestion(patientRefId, suggestionId)
      const wasApproved = reviewState[suggestionId]?.status === 'approved'
      setReviewState((prev) => {
        const next = { ...prev }
        delete next[suggestionId]
        return next
      })
      // Undoing an approved measurement deleted a record — keep the deferred refresh armed.
      if (wasApproved) pendingRefreshRef.current = true
    } catch (err: any) {
      console.error('Undo failed:', err)
      toast.error(err.response?.data?.error?.message || 'Undo failed')
    } finally {
      setApproving(false)
    }
  }

  const handleUploadLabs = () => {
    setExtractMenuAnchor(null)
    fileInputRef.current?.click()
  }

  // Stage files (used by both file input and drag-drop)
  const stageFiles = (files: File[]) => {
    if (files.length === 0) return

    // Filter to only PDF files
    const pdfFiles = files.filter((f) => f.name.toLowerCase().endsWith('.pdf'))
    if (pdfFiles.length === 0) {
      toast.error('Only PDF files are allowed')
      return
    }

    if (pdfFiles.length !== files.length) {
      toast.warning(`Skipped ${files.length - pdfFiles.length} non-PDF file(s)`)
    }

    // Add to staged files, avoiding duplicates by name
    setStagedFiles((prev) => {
      const existingNames = new Set(prev.map((f) => f.name))
      const newFiles = pdfFiles.filter((f) => !existingNames.has(f.name))
      if (newFiles.length < pdfFiles.length) {
        toast.info(`${pdfFiles.length - newFiles.length} file(s) already staged`)
      }
      return [...prev, ...newFiles]
    })

    toast.success(`Added ${pdfFiles.length} file(s) to queue`)
  }

  // Remove a file from staged list
  const removeStagedFile = (fileName: string) => {
    setStagedFiles((prev) => prev.filter((f) => f.name !== fileName))
  }

  // Clear all staged files
  const clearStagedFiles = () => {
    setStagedFiles([])
  }

  // Process all staged files
  const processStagedFiles = async () => {
    if (stagedFiles.length === 0) return

    setUploading(true)
    setError(null)

    try {
      // Upload with process_immediately=true to extract right after upload
      const response = await aiExtractionService.uploadLabPdfs(patientRefId, stagedFiles, true)

      const uploadedCount = response.data.file_count || stagedFiles.length
      const extractionResult = response.data.extraction_result
      const errors = extractionResult?.errors

      // A PARTIAL read is never reported as a whole one (CAPA F272). The backend answers 207
      // and sends structured `coverage`; axios resolves 2xx as success, so without this branch
      // an incomplete read lands in the success toast below and the reviewer sees green plus a
      // measurement count — the exact signal that hid a 20-of-94-page read. Branch on the
      // FIELD, not on the wording of an error string, and use the persistent banner rather
      // than a toast: a completeness warning must survive longer than four seconds.
      const incomplete = (extractionResult?.coverage || []).filter((c) => !c.complete)
      setIncompleteRead(
        incomplete.length === 0
          ? null
          : incomplete
              .map((c) =>
                c.pages_total === null
                  ? `${c.file}: not read at all${c.error ? ` — ${c.error}` : ''}`
                  : `${c.file}: only ${c.pages_read} of ${c.pages_total} pages were read` +
                    `${c.pages_unread.length ? ` (missing ${c.pages_unread.join(', ')})` : ''}`
              )
              .join(' · ')
      )

      // Check for duplicate detection
      if (errors && errors.length > 0 && errors.some((e: string) => e.includes('already processed'))) {
        toast.warning('This lab report has already been processed. No new data was extracted.')
      } else if (incomplete.length > 0) {
        toast.warning(
          `Processed ${uploadedCount} file(s) — INCOMPLETE. See the banner: part of the ` +
            `document was not read, so these suggestions are not the whole record.`
        )
      } else {
        const totalExtracted = extractionResult?.total_suggestions ?? 0
        if (totalExtracted > 0) {
          toast.success(`Processed ${uploadedCount} file(s), extracted ${totalExtracted} measurements`)
        } else {
          toast.success(`Processed ${uploadedCount} file(s). No measurements extracted.`)
        }
      }

      // Clear staged files after successful processing
      setStagedFiles([])
      await loadSuggestions()
    } catch (err: any) {
      console.error('Processing failed:', err)
      const errMsg = err.response?.data?.error?.message || 'Processing failed'
      setError(errMsg)
      toast.error(errMsg)
    } finally {
      setUploading(false)
    }
  }

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    stageFiles(Array.from(files))

    // Reset file input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current += 1
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragOver(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current -= 1
    if (dragCounter.current === 0) {
      setIsDragOver(false)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    dragCounter.current = 0

    const files = Array.from(e.dataTransfer.files) as File[]
    stageFiles(files)
  }

  const toggleSelection = (id: number) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedIds(newSelected)
  }

  const toggleExpanded = (id: number) => {
    const newExpanded = new Set(expandedIds)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedIds(newExpanded)
  }

  const selectAll = () => {
    const filtered = getFilteredSuggestions()
    setSelectedIds(new Set(filtered.map((s) => s.id)))
  }

  const deselectAll = () => {
    setSelectedIds(new Set())
  }

  const getFilteredSuggestions = (): AIExtractedSuggestion[] => {
    if (activeTab === 'all') return suggestions
    return suggestions.filter((s) => s.suggestion_type === activeTab)
  }

  const filteredSuggestions = getFilteredSuggestions()
  const diagnosisCount = suggestions.filter((s) => s.suggestion_type === 'diagnosis').length
  const measurementCount = suggestions.filter((s) => s.suggestion_type === 'measurement').length

  const formatDate = (timestamp: number | null | undefined): string => {
    if (!timestamp) return 'N/A'
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  // Structured reject picker — category chips (required) + optional correction/note.
  // Rendered inline in a card (per-item) or in the bulk-actions row (multi-select).
  const renderRejectForm = (targetIds: number[]) => {
    const cats = getRejectCategories(targetIds)
    const selectedCat = cats.find((c) => c.code === rejectCategory)
    const isSingle = targetIds.length === 1
    const showCorrection = isSingle && (selectedCat?.allowsCorrection || rejectCategory === 'other')
    const correctionLabel = rejectCategory === 'other' ? 'Add a note (optional)' : 'What should it have been? (optional)'

    return (
      <Box sx={{ mt: 0.5, p: 1, border: '1px solid', borderColor: 'error.light', borderRadius: 0.5, bgcolor: 'rgba(211,47,47,0.04)' }}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          Why reject{isSingle ? '' : ` ${targetIds.length} items`}?
        </Typography>
        <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.5 }}>
          {cats.map((c) => (
            <Chip
              key={c.code}
              label={c.label}
              size="small"
              color={rejectCategory === c.code ? 'error' : 'default'}
              variant={rejectCategory === c.code ? 'filled' : 'outlined'}
              onClick={() => setRejectCategory(c.code)}
              sx={{ height: 22, fontSize: '0.65rem' }}
            />
          ))}
        </Stack>
        {showCorrection && (
          <TextField
            fullWidth
            size="small"
            variant="standard"
            placeholder={correctionLabel}
            value={rejectCorrection}
            onChange={(e) => setRejectCorrection(e.target.value)}
            sx={{ mt: 0.5, '& input': { fontSize: '0.7rem' } }}
          />
        )}
        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
          <Button
            className={classes.diagnosisPlanBtn}
            color="error"
            variant="contained"
            disabled={!rejectCategory || approving}
            onClick={confirmReject}
            sx={{ fontSize: '0.65rem' }}
          >
            Confirm reject
          </Button>
          <Button className={classes.diagnosisPlanBtn} variant="text" disabled={approving} onClick={cancelReject} sx={{ fontSize: '0.65rem' }}>
            Cancel
          </Button>
        </Stack>
      </Box>
    )
  }

  const renderSuggestionCard = (suggestion: AIExtractedSuggestion) => {
    const isSelected = selectedIds.has(suggestion.id)
    const isExpanded = expandedIds.has(suggestion.id)
    const isDiagnosis = suggestion.suggestion_type === 'diagnosis'
    const review = reviewState[suggestion.id]
    const isRejectingThis = !!rejectTarget && rejectTarget.length === 1 && rejectTarget[0] === suggestion.id

    return (
      <Card
        key={suggestion.id}
        variant="outlined"
        sx={{
          mb: 1,
          borderColor: review ? (review.status === 'approved' ? 'success.light' : 'error.light') : isSelected ? 'primary.main' : 'divider',
          borderWidth: isSelected && !review ? 2 : 1,
          bgcolor: review ? (review.status === 'approved' ? 'rgba(46,125,50,0.04)' : 'rgba(211,47,47,0.04)') : 'transparent',
        }}
      >
        <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
          <Stack direction="row" alignItems="flex-start" spacing={0.5}>
            <Checkbox checked={isSelected} onChange={() => toggleSelection(suggestion.id)} size="small" sx={{ p: 0.25 }} />

            <Box flex={1} sx={{ minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                {isDiagnosis ? <DiagnosisIcon sx={{ fontSize: 14, color: 'primary.main' }} /> : <MeasurementIcon sx={{ fontSize: 14, color: 'secondary.main' }} />}
                <Typography variant="body2" fontWeight="medium" noWrap>
                  {/* Curated KB display_name, prefix-stripped. The slot name is the
                      fallback only: de-underscoring it yields "carpal tunnel syndrome
                      minim", where `minim` is a KB naming suffix (RD 2026-08-08). */}
                  {isDiagnosis
                    ? (formatDisplayName(suggestion.display_name) || suggestion.slot_name?.replace(/_/g, ' '))
                    : suggestion.measurement_name?.replace(/_/g, ' ')}
                </Typography>
                <Chip label={isDiagnosis ? 'Dx' : 'Meas'} size="small" color={isDiagnosis ? 'primary' : 'secondary'} variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
              </Stack>

              {isDiagnosis ? (
                <Box sx={{ mt: 0.25 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {suggestion.questionnaire_name}
                  </Typography>
                  <Typography variant="caption">
                    <strong>Answer:</strong> {suggestion.proposed_answer}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ mt: 0.25 }}>
                  <Typography variant="caption" sx={{ color: suggestion.is_abnormal ? 'var(--color-error) !important' : 'inherit' }}>
                    <strong>{suggestion.measurement_value}</strong> {suggestion.measurement_unit}
                    {suggestion.measurement_date ? (
                      <span style={{ color: suggestion.is_abnormal ? 'inherit' : '#666', marginLeft: 8 }}>{formatDate(suggestion.measurement_date)}</span>
                    ) : null}
                  </Typography>
                  {/* The report stated no collection date for this value. It is NOT chartable
                      undated — filing it as of today would make a possibly-stale result this
                      patient's current value for the marker. The reviewer has the source PDF,
                      so they read the real "Date Collected" off it here. */}
                  {!suggestion.measurement_date && (
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'var(--color-error)', fontWeight: 600 }}>
                        No date on report —
                      </Typography>
                      <TextField
                        type="date"
                        size="small"
                        value={dateEdits[suggestion.id] || ''}
                        onChange={(e) =>
                          setDateEdits((prev) => ({ ...prev, [suggestion.id]: e.target.value }))
                        }
                        inputProps={{
                          max: new Date().toISOString().slice(0, 10),  // a future draw is not a draw
                          'aria-label': `Collection date for ${suggestion.measurement_name}`,
                        }}
                        sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.4 } }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        collection date, from the report
                      </Typography>
                    </Stack>
                  )}
                </Box>
              )}

              {/* Source Evidence */}
              {suggestion.source_text_excerpts && suggestion.source_text_excerpts.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                  <Stack direction="row" alignItems="center" onClick={() => toggleExpanded(suggestion.id)} sx={{ cursor: 'pointer' }}>
                    <DocumentIcon sx={{ fontSize: 12, mr: 0.25, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      Evidence ({suggestion.source_text_excerpts.length})
                    </Typography>
                    <IconButton size="small" sx={{ p: 0, ml: 0.25 }}>
                      {isExpanded ? <CollapseIcon sx={{ fontSize: 14 }} /> : <ExpandIcon sx={{ fontSize: 14 }} />}
                    </IconButton>
                  </Stack>

                  <Collapse in={isExpanded}>
                    <Paper
                      elevation={0}
                      sx={{
                        mt: 0.5,
                        p: 1,
                        bgcolor: 'transparent',
                        border: '1px solid var(--color-border-light)',
                        borderRadius: 0.5,
                        maxHeight: 120,
                        overflow: 'auto',
                      }}
                    >
                      {suggestion.source_text_excerpts.map((excerpt, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            color: 'var(--color-text-primary)',
                            mb: idx < suggestion.source_text_excerpts.length - 1 ? 0.5 : 0,
                            pl: 0.5,
                            borderLeft: '2px solid',
                            borderColor: 'primary.light',
                            fontSize: '0.75rem',
                            '& *': { color: 'var(--color-text-primary)' },
                            '& p': { margin: 0 },
                            '& h1, & h2, & h3, & h4, & h5, & h6': {
                              margin: '0.25rem 0',
                              fontSize: '0.8rem',
                              fontWeight: 600,
                            },
                            '& ul, & ol': {
                              margin: '0.25rem 0',
                              paddingLeft: '1.25rem',
                            },
                            '& li': { marginBottom: '0.125rem' },
                            '& strong': {
                              fontWeight: 700,
                              backgroundColor: 'rgba(255, 235, 59, 0.3)',
                              padding: '0 0.15rem',
                              borderRadius: '2px',
                            },
                            '& code': {
                              backgroundColor: 'rgba(128,128,128,0.2)',
                              padding: '0.1rem 0.25rem',
                              borderRadius: '2px',
                              fontSize: '0.7rem',
                            },
                            '& table': {
                              borderCollapse: 'collapse',
                              width: '100%',
                              fontSize: '0.7rem',
                              marginTop: '0.25rem',
                            },
                            '& th, & td': {
                              border: '1px solid var(--color-border-light)',
                              padding: '0.25rem 0.5rem',
                              textAlign: 'left',
                            },
                            '& th': {
                              fontWeight: 600,
                              backgroundColor: 'rgba(128,128,128,0.1)',
                            },
                          }}
                        >
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{excerpt}</ReactMarkdown>
                        </Box>
                      ))}
                    </Paper>

                    {/* Source region — text-layer-grounded crop of the exact PDF region the
                        value was read from (no-black-box visual confirmation, RD 2026-07-25).
                        Nested inside the Evidence collapse so text + crop reveal together. */}
                    {suggestion.source_image_excerpts && suggestion.source_image_excerpts.length > 0 && (
                      <Box sx={{ mt: 0.75 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                          Source region{suggestion.source_image_excerpts.length > 1 ? 's' : ''}
                          {suggestion.source_vendor ? ` · ${suggestion.source_vendor}` : ''}
                        </Typography>
                        {suggestion.source_image_excerpts.map((ex, idx) => (
                          <img
                            key={idx}
                            src={ex.image}
                            alt="source region"
                            title="Click to enlarge"
                            onClick={() => setZoomImage(ex.image)}
                            style={{
                              maxWidth: '100%',
                              border: '1px solid var(--color-border-light)',
                              borderRadius: 4,
                              display: 'block',
                              marginBottom: 4,
                              cursor: 'zoom-in',
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Collapse>
                </Box>
              )}

              {/* Reviewed state — stays in place; never refetches the list */}
              {review ? (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                  {review.status === 'approved' ? (
                    <Chip icon={<ApproveIcon sx={{ fontSize: '0.8rem !important' }} />} label="Approved" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                  ) : (
                    <Chip icon={<RejectIcon sx={{ fontSize: '0.8rem !important' }} />} label={`Rejected${review.categoryLabel ? `: ${review.categoryLabel}` : ''}`} size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                  )}
                  {review.canUndo && (
                    <Button
                      className={classes.diagnosisPlanBtn}
                      variant="text"
                      startIcon={<UndoIcon sx={{ fontSize: '0.8rem !important' }} />}
                      onClick={() => handleUndo(suggestion.id)}
                      disabled={approving}
                      sx={{ fontSize: '0.65rem' }}
                    >
                      Undo
                    </Button>
                  )}
                </Stack>
              ) : isRejectingThis ? (
                renderRejectForm([suggestion.id])
              ) : (
                /* Action buttons inline */
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                  <Button
                    className={classes.diagnosisPlanBtn}
                    color="success"
                    variant="text"
                    startIcon={<ApproveIcon sx={{ fontSize: '0.8rem !important' }} />}
                    onClick={() => handleApprove([suggestion.id])}
                    disabled={approving}
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Approve
                  </Button>
                  <Button
                    className={classes.diagnosisPlanBtn}
                    color="error"
                    variant="text"
                    startIcon={<RejectIcon sx={{ fontSize: '0.8rem !important' }} />}
                    onClick={() => openRejectPicker([suggestion.id])}
                    disabled={approving}
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Reject
                  </Button>
                </Stack>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    )
  }

  return (
    <Box
      sx={{
        px: 1, py: 0.5, position: 'relative', minHeight: 200,
        // This section renders at MUI's default type scale while every card around it
        // sits at ~0.75rem, so it read as oversized next to its neighbours. Scaled at the
        // container, matching experiment-dialog, rather than by editing each Typography —
        // the descendant selectors also catch the MUI defaults (Tabs, Buttons) that carry
        // no explicit size of their own (RD 2026-08-08).
        '& .MuiTypography-root': { fontSize: '0.75rem' },
        '& .MuiTypography-caption': { fontSize: '0.7rem' },
        '& .MuiButton-root': { fontSize: '0.7rem' },
        '& .MuiTab-root': { fontSize: '0.7rem', minHeight: 32, py: 0.5 },
        '& .MuiInputBase-input': { fontSize: '0.75rem' },
        '& .MuiInputLabel-root': { fontSize: '0.75rem' },
        '& .MuiFormControlLabel-label': { fontSize: '0.72rem' },
        '& .MuiChip-root': { fontSize: '0.62rem' },
      }}
      onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDragOver={handleDragOver} onDrop={handleDrop}
    >
      {/* Zoomed source-region crop — click any crop to read the marker name at full resolution */}
      <Dialog open={!!zoomImage} onClose={() => setZoomImage(null)} maxWidth="lg">
        <DialogContent sx={{ p: 1 }}>
          {zoomImage && (
            <img
              src={zoomImage}
              alt="source region enlarged"
              onClick={() => setZoomImage(null)}
              style={{ maxWidth: '100%', maxHeight: '85vh', display: 'block', cursor: 'zoom-out' }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Drag and drop overlay */}
      {isDragOver && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(25, 118, 210, 0.1)',
            border: '2px dashed',
            borderColor: 'primary.main',
            borderRadius: 1,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Stack alignItems="center" spacing={0.5}>
            <UploadIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="body2" color="primary.main" fontWeight="medium">
              Drop PDF files here
            </Typography>
          </Stack>
        </Box>
      )}

      {/* Hidden file input for lab PDF upload */}
      <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept=".pdf" multiple onChange={handleFileSelected} />

      {/* Action buttons */}
      <Stack direction="row" spacing={0.5} mb={1} justifyContent="flex-end">
        <Button
          className={classes.diagnosisPlanBtn}
          variant="text"
          startIcon={<RefreshIcon sx={{ fontSize: '0.8rem !important' }} />}
          onClick={loadSuggestions}
          disabled={loading}
          sx={{ fontSize: '0.65rem' }}
        >
          Refresh
        </Button>
        <Button
          className={classes.diagnosisPlanBtn}
          variant="outlined"
          color="primary"
          startIcon={extracting || uploading ? <CircularProgress size={10} color="inherit" /> : <ExtractIcon sx={{ fontSize: '0.8rem !important' }} />}
          endIcon={<ArrowDownIcon sx={{ fontSize: '0.8rem !important' }} />}
          onClick={(e) => setExtractMenuAnchor(e.currentTarget)}
          disabled={extracting || uploading}
          sx={{ fontSize: '0.65rem' }}
        >
          {uploading ? 'Uploading...' : 'Extract'}
        </Button>
        <Menu anchorEl={extractMenuAnchor} open={Boolean(extractMenuAnchor)} onClose={() => setExtractMenuAnchor(null)}>
          <MenuItem onClick={() => handleExtract('all')} sx={{ fontSize: '0.8rem' }}>
            All (Diagnoses, Measurements & Allergies)
          </MenuItem>
          <MenuItem onClick={() => handleExtract('dx')} sx={{ fontSize: '0.8rem' }}>
            Diagnoses Only
          </MenuItem>
          <MenuItem onClick={() => handleExtract('meas')} sx={{ fontSize: '0.8rem' }}>
            Measurements Only
          </MenuItem>
          <MenuItem onClick={() => handleExtract('allergy')} sx={{ fontSize: '0.8rem' }}>
            Allergies Only
          </MenuItem>
        </Menu>
      </Stack>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 1, py: 0 }} onClose={() => setError(null)}>
          <Typography variant="caption">{error}</Typography>
        </Alert>
      )}

      {/* Incomplete read — part of the source was never read, so the suggestions below are not
          the whole record. Deliberately NOT dismissible: it qualifies every suggestion in the
          list, so it stays until the next extraction replaces it (CAPA F272). */}
      {incompleteRead && (
        <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
          <Typography variant="caption">
            <strong>Incomplete read — these suggestions are not the whole document.</strong>{' '}
            {incompleteRead}. Any result printed on the unread pages is missing from this list.
          </Typography>
        </Alert>
      )}

      {/* Extraction Log Summary */}
      {extractionLog && (
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Last: {formatDate(extractionLog.extraction_timestamp)}
          </Typography>
          <Chip label={`${extractionLog.total_suggestions} total`} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
          <Chip label={`${extractionLog.approved_count} approved`} size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />
          <Chip label={`${extractionLog.rejected_count} rejected`} size="small" color="error" sx={{ height: 18, fontSize: '0.65rem' }} />
          {extractionLog.is_reviewed && <Chip label="Done" size="small" color="info" sx={{ height: 18, fontSize: '0.65rem' }} />}
        </Stack>
      )}

      {/* Loading State */}
      {loading && <LinearProgress sx={{ mb: 1 }} />}

      {/* No Suggestions - show drop zone hint */}
      {!loading && suggestions.length === 0 && (
        <Box>
          <Alert severity="info" sx={{ py: 0.5, mb: 1 }}>
            <Typography variant="caption">No pending suggestions. Click "Extract" to process patient documents.</Typography>
          </Alert>

          {/* Drop zone hint */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: '2px dashed',
              borderColor: isDragOver ? 'primary.main' : 'grey.300',
              borderRadius: 1,
              bgcolor: isDragOver ? 'rgba(25, 118, 210, 0.05)' : 'grey.50',
              textAlign: 'center',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'primary.light',
                bgcolor: 'rgba(25, 118, 210, 0.02)',
              },
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <UploadIcon
              sx={{
                fontSize: 32,
                color: isDragOver ? 'primary.main' : 'grey.400',
                mb: 0.5,
              }}
            />
            <Typography variant="body2" color={isDragOver ? 'primary.main' : 'text.secondary'}>
              {isDragOver ? 'Drop PDF files here' : 'Drag & drop lab PDFs here'}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              or click to browse
            </Typography>
          </Paper>

          {/* Staged files list */}
          {stagedFiles.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="body2" fontWeight="medium">
                  Queued Files ({stagedFiles.length})
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  <Button className={classes.diagnosisPlanBtn} variant="text" onClick={clearStagedFiles} disabled={uploading} sx={{ fontSize: '0.65rem' }}>
                    Clear All
                  </Button>
                  <Button
                    className={classes.diagnosisPlanBtn}
                    variant="outlined"
                    startIcon={uploading ? <CircularProgress size={10} color="inherit" /> : <ExtractIcon sx={{ fontSize: '0.8rem !important' }} />}
                    onClick={processStagedFiles}
                    disabled={uploading || stagedFiles.length < 1}
                    sx={{ fontSize: '0.65rem', py: 0.25 }}
                  >
                    {uploading ? 'Processing...' : `Process (${stagedFiles.length})`}
                  </Button>
                </Stack>
              </Stack>
              <Paper variant="outlined" sx={{ maxHeight: 150, overflow: 'auto' }}>
                {stagedFiles.map((file, idx) => (
                  <Stack
                    key={file.name}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderBottom: idx < stagedFiles.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      '&:hover': { bgcolor: 'grey.50' },
                    }}
                  >
                    <PdfIcon sx={{ fontSize: 16, color: 'error.main' }} />
                    <Typography
                      variant="caption"
                      sx={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
                      {(file.size / 1024).toFixed(0)} KB
                    </Typography>
                    <IconButton size="small" onClick={() => removeStagedFile(file.name)} disabled={uploading} sx={{ p: 0.25 }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Stack>
                ))}
              </Paper>
            </Box>
          )}
        </Box>
      )}

      {/* Suggestions List */}
      {suggestions.length > 0 && (
        <>
          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{
              minHeight: 32,
              mb: 1,
              '& .MuiTab-root': { minHeight: 32, py: 0, fontSize: '0.75rem' },
            }}
          >
            <Tab label={`All (${suggestions.length})`} value="all" />
            <Tab label={`Dx (${diagnosisCount})`} value="diagnosis" />
            <Tab label={`Meas (${measurementCount})`} value="measurement" />
          </Tabs>

          {/* Bulk Actions */}
          {filteredSuggestions.length > 0 && (
            <Stack direction="row" spacing={0.5} mb={1} alignItems="center" flexWrap="wrap">
              <Button className={classes.diagnosisPlanBtn} onClick={selectAll} sx={{ fontSize: '0.65rem' }}>
                Select All
              </Button>
              <Button className={classes.diagnosisPlanBtn} onClick={deselectAll} sx={{ fontSize: '0.65rem' }}>
                Clear
              </Button>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <Button
                className={classes.diagnosisPlanBtn}
                color="success"
                variant="outlined"
                startIcon={<ApproveIcon sx={{ fontSize: '0.8rem !important' }} />}
                onClick={() => handleApprove(Array.from(selectedIds))}
                disabled={selectedIds.size === 0 || approving}
                sx={{ fontSize: '0.65rem' }}
              >
                Approve ({selectedIds.size})
              </Button>
              <Button
                className={classes.diagnosisPlanBtn}
                color="error"
                variant="outlined"
                startIcon={<RejectIcon sx={{ fontSize: '0.8rem !important' }} />}
                onClick={() => openRejectPicker(Array.from(selectedIds))}
                disabled={selectedIds.size === 0 || approving}
                sx={{ fontSize: '0.65rem' }}
              >
                Reject ({selectedIds.size})
              </Button>
            </Stack>
          )}

          {/* Bulk reject picker (multi-select) */}
          {rejectTarget && rejectTarget.length > 1 && <Box sx={{ mb: 1 }}>{renderRejectForm(rejectTarget)}</Box>}

          {/* Suggestion Cards */}
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>{filteredSuggestions.map(renderSuggestionCard)}</Box>

          {/* Staged files or drop zone hint at bottom */}
          {stagedFiles.length > 0 ? (
            <Box sx={{ mt: 1 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
                <Typography variant="caption" fontWeight="medium">
                  Queued Files ({stagedFiles.length})
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  <Button className={classes.diagnosisPlanBtn} variant="text" onClick={clearStagedFiles} disabled={uploading} sx={{ fontSize: '0.65rem' }}>
                    Clear
                  </Button>
                  <Button
                    className={classes.diagnosisPlanBtn}
                    variant="outlined"
                    startIcon={uploading ? <CircularProgress size={10} color="inherit" /> : <ExtractIcon sx={{ fontSize: '0.8rem !important' }} />}
                    onClick={processStagedFiles}
                    disabled={uploading || stagedFiles.length < 1}
                    sx={{ fontSize: '0.65rem', py: 0.25 }}
                  >
                    {uploading ? 'Processing...' : `Process (${stagedFiles.length})`}
                  </Button>
                </Stack>
              </Stack>
              <Paper variant="outlined" sx={{ maxHeight: 100, overflow: 'auto' }}>
                {stagedFiles.map((file, idx) => (
                  <Stack
                    key={file.name}
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{
                      px: 0.5,
                      py: 0.25,
                      borderBottom: idx < stagedFiles.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                    }}
                  >
                    <PdfIcon sx={{ fontSize: 12, color: 'error.main' }} />
                    <Typography
                      variant="caption"
                      sx={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.65rem',
                      }}
                    >
                      {file.name}
                    </Typography>
                    <IconButton size="small" onClick={() => removeStagedFile(file.name)} disabled={uploading} sx={{ p: 0.125 }}>
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Stack>
                ))}
              </Paper>
              {/* Small drop zone below staged files */}
              <Box
                sx={{
                  mt: 0.5,
                  p: 0.5,
                  border: '1px dashed',
                  borderColor: isDragOver ? 'primary.main' : 'grey.300',
                  borderRadius: 0.5,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.light' },
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>
                  + Add more files
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                mt: 1,
                p: 1,
                border: '1px dashed',
                borderColor: isDragOver ? 'primary.main' : 'grey.300',
                borderRadius: 1,
                bgcolor: isDragOver ? 'rgba(25, 118, 210, 0.05)' : 'transparent',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'primary.light',
                  bgcolor: 'rgba(25, 118, 210, 0.02)',
                },
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                <UploadIcon
                  sx={{
                    fontSize: 14,
                    color: isDragOver ? 'primary.main' : 'grey.400',
                  }}
                />
                <Typography variant="caption" color={isDragOver ? 'primary.main' : 'text.disabled'}>
                  {isDragOver ? 'Drop PDF files here' : 'Drop lab PDFs here or click to upload'}
                </Typography>
              </Stack>
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

export default withStyles(stylesheet)(AIReviewComponent)
